import type { Columns } from '../Steps/MatchColumns';
import { ColumnType } from '../Steps/MatchColumns';
import { normalizeCheckboxValue } from './normalizeCheckboxValue';
import { RawData, Fields, Data } from '../../types';

// Campos numéricos que devem ser sanitizados
const NUMERIC_FIELDS = ['value', 'quantity', 'mileage'];

// Sanitiza valores numéricos removendo caracteres não numéricos
const sanitizeNumericValue = (value: any): string | undefined => {
  if (value === '' || value === null || value === undefined) return undefined;
  const str = String(value);
  // Remove tudo exceto números, ponto, vírgula e sinal negativo
  return str.replace(/[^0-9.,-]/g, '');
};

export const normalizeTableData = <T extends string>(columns: Columns<T>, data: RawData[], fields: Fields<T>) =>
  data.map(row =>
    columns.reduce((acc, column, index) => {
      const curr = row[index];
      switch (column.type) {
        case ColumnType.matchedCheckbox: {
          const field = fields.find(field => field.key === column.value)!;
          if ('booleanMatches' in field.fieldType && Object.keys(field.fieldType).length) {
            const booleanMatchKey = Object.keys(field.fieldType.booleanMatches || []).find(
              key => key.toLowerCase() === curr?.toLowerCase(),
            )!;
            const booleanMatch = field.fieldType.booleanMatches?.[booleanMatchKey];
            acc[column.value] = booleanMatchKey ? booleanMatch : normalizeCheckboxValue(curr);
          } else {
            acc[column.value] = normalizeCheckboxValue(curr);
          }
          return acc;
        }
        case ColumnType.matched: {
          // Sanitiza campos numéricos (remove símbolos como €, letras, etc.)
          if (NUMERIC_FIELDS.includes(column.value)) {
            acc[column.value] = sanitizeNumericValue(curr);
          } else {
            acc[column.value] = curr === '' ? undefined : curr;
          }
          // Propagar formato de data para campos input com matchedOptions (onlyOneOption)
          if (column.value === 'date' && 'matchedOptions' in column && column.matchedOptions) {
            const dateFormat = (column.matchedOptions as any[]).find(mo => !!mo.value)?.value;
            if (dateFormat) acc = { ...acc, dateFormat };
          } else if (column.value === 'paymentDate' && 'matchedOptions' in column && column.matchedOptions) {
            const datePaymentFormat = (column.matchedOptions as any[]).find(mo => !!mo.value)?.value;
            if (datePaymentFormat) acc = { ...acc, datePaymentFormat };
          }
          return acc;
        }
        case ColumnType.matchedSelect:
        case ColumnType.matchedSelectOptions: {
          const matchedOption = column.matchedOptions.find(({ entry, value }) => entry === curr);

          if (column.value === 'date') {
            const dateFormat = column.matchedOptions.find(mo => !!mo.value)?.value;
            acc[column.value] = curr === '' ? undefined : curr;
            acc = { ...acc, dateFormat }

          }
          else if (column.value === 'paymentDate') {
            const datePaymentFormat = column.matchedOptions.find(mo => !!mo.value)?.value;
            acc[column.value] = curr === '' ? undefined : curr;
            acc = { ...acc, datePaymentFormat }

          }
          else {
            acc[column.value] = matchedOption?.value || undefined;
            // Só define isFuelType quando a coluna é de tipo de despesa (typeCode)
            if (column.value === 'typeCode') {
              acc = { ...acc, isFuelType: matchedOption?.isFuelType ?? false };
            }
            // Preservar o nome original do condutor do Excel
            if (column.value === 'responsible') {
              acc = { ...acc, responsibleOriginalName: matchedOption?.entry || undefined };
            }
          }

          return acc;

        }
        case ColumnType.empty:
        case ColumnType.ignored: {
          return acc;
        }
        default:
          return acc;
      }
    }, {} as Data<T>),
  );
